import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import metricTypeCheckers from '../types/metricTypeCheckers';
import getQueryAlias from './getQueryAlias';
import buildAggregateCompoundMetricQuery from './buildAggregateCompoundMetricQuery';

const isMetricInput = (
  metric: MetricInput | Metrics.SpecialMetric,
): metric is MetricInput => {
  if ('type' in metric) {
    return false;
  }
  return true;
};

const aggregateMetricMatrix = async ({
  client,
  metrics,
  expressions,
  filterInput,
  dateScope,
  groupBy,
  subGroupBy,
  trendByCalendarInterval,
  trendByFixedIntervalDays,
  trendByFiscalInterval,
  groupByDayOfWeek,
  groupByRange,
  groupByInterval,
  groupByHour,
  groupByMonth,
  forecastPeriods,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  metrics: (MetricInput | Metrics.SpecialMetric)[];
  expressions: Expression2[];
  filterInput: FilterInput[];
  dateScope: DateRangeInput;
  groupBy?: V5GroupBy;
  subGroupBy?: V5GroupBy;
  trendByCalendarInterval?: FleetOps.Interval;
  trendByFixedIntervalDays?: number;
  trendByFiscalInterval?: FixedPeriods.FiscalType;
  groupByDayOfWeek?: boolean;
  groupByRange?: V5RangeAggregation;
  groupByInterval?: V5Histogram;
  groupByHour?: boolean;
  groupByMonth?: boolean;
  forecastPeriods?: number;
  chartDefId?: string; // for mocking purposes
}): Promise<MetricsResponse> => {
  const maybeSpecialMetric = metrics.find(metricTypeCheckers.isSpecialMetric);
  const alias = maybeSpecialMetric
    ? maybeSpecialMetric.query
    : getQueryAlias({
        groupBy,
        subGroupBy,
        trendByCalendarInterval,
        trendByFixedIntervalDays,
        groupByDayOfWeek,
        groupByRange,
        groupByInterval,
        trendByFiscalInterval,
      });

  const queryName = maybeSpecialMetric
    ? maybeSpecialMetric.query
    : 'aggregateCompoundMetric';
  const sanitizedMetrics = metrics.filter(isMetricInput);

  const query = buildAggregateCompoundMetricQuery({
    metrics: maybeSpecialMetric ? [] : sanitizedMetrics,
    expressions,
    filterInput,
    dateScope,
    query: queryName,
    alias,
    groupBy,
    // Special-metric resolvers don't support a second dimension, so never send
    // subGroupBy for them (it would fail GraphQL validation).
    subGroupBy: maybeSpecialMetric ? undefined : subGroupBy,
    trendByCalendarInterval,
    trendByFixedIntervalDays,
    trendByFiscalInterval,
    groupByDayOfWeek,
    groupByRanges: groupByRange,
    groupByInterval,
    groupByHour,
    groupByMonth,
    // Special-metric resolvers don't forecast
    forecastPeriods: maybeSpecialMetric ? undefined : forecastPeriods,
  });

  const request = client.query(query);
  const response = await request;
  const data = response.data[queryName] as MetricsResponse;
  return data.map((d) => ({
    ...d,
    filterInput: filterInput[0],
    dateScope,
  }));
};

export default aggregateMetricMatrix;
